import { useState } from 'react';

export default function FinalCTA() {
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [showAuditForm, setShowAuditForm] = useState(false);
  const [consultationFormData, setConsultationFormData] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [auditFormData, setAuditFormData] = useState({ name: '', email: '', company: '', phone: '', industry: '', message: '' });
  const [consultationSubmitting, setConsultationSubmitting] = useState(false);
  const [auditSubmitting, setAuditSubmitting] = useState(false);
  const [consultationSuccess, setConsultationSuccess] = useState(false);
  const [auditSuccess, setAuditSuccess] = useState(false);

  const handleConsultationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setConsultationSubmitting(true);
    try {
      const formBody = new URLSearchParams();
      Object.entries(consultationFormData).forEach(([k, v]) => formBody.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d66bgtskan35qnu5ikm0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      if (res.ok) {
        setConsultationSuccess(true);
        setConsultationFormData({ name: '', email: '', company: '', phone: '', message: '' });
        setTimeout(() => { setShowConsultationForm(false); setConsultationSuccess(false); }, 3000);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setConsultationSubmitting(false);
    }
  };

  const handleAuditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuditSubmitting(true);
    try {
      const formBody = new URLSearchParams();
      Object.entries(auditFormData).forEach(([k, v]) => formBody.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d66bgtskan35qnu5ikmg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      if (res.ok) {
        setAuditSuccess(true);
        setAuditFormData({ name: '', email: '', company: '', phone: '', industry: '', message: '' });
        setTimeout(() => { setShowAuditForm(false); setAuditSuccess(false); }, 3000);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setAuditSubmitting(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm text-slate-800 placeholder-slate-400 transition-all';

  return (
    <section id="consultation" className="relative bg-[#0a0f1c] py-40 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/4fdaa11f6bd40aade04b00ccb0dea214.jpg"
          alt=""
          className="w-full h-full object-cover object-center opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f1c]/70 via-[#0a0f1c]/50 to-[#0a0f1c]/80" />
      </div>

      <div className="relative max-w-3xl mx-auto px-8 text-center">
        <p className="text-teal-400 text-xs font-semibold tracking-[0.2em] uppercase mb-6">Your Next Step</p>
        <h2 className="font-serif text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
          Operate at<br />the Summit.
        </h2>
        <p className="text-white/50 text-xl font-light mb-14 max-w-xl mx-auto leading-relaxed">
          If your business demands higher standards of performance, clarity, and structure — Santislogix is your intelligence partner.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => setShowConsultationForm(true)}
            className="bg-teal-500 text-white px-10 py-4 rounded-full text-base font-medium hover:bg-teal-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Schedule a Strategic Elevation Call
          </button>
          <button
            onClick={() => setShowAuditForm(true)}
            className="border border-white/20 text-white/80 px-10 py-4 rounded-full text-base font-light hover:bg-white/5 hover:text-white transition-all whitespace-nowrap cursor-pointer"
          >
            Request a Systems Audit
          </button>
        </div>
      </div>

      {showConsultationForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowConsultationForm(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-serif text-2xl font-bold text-slate-900">Strategic Elevation Call</h3>
              <button onClick={() => setShowConsultationForm(false)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer rounded-full hover:bg-slate-100 transition-colors">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            {consultationSuccess ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-5xl text-teal-500" />
                </div>
                <p className="text-slate-700 font-medium">Thank you! We&apos;ll be in touch soon.</p>
              </div>
            ) : (
              <form id="consultation-form" data-readdy-form onSubmit={handleConsultationSubmit} className="space-y-4">
                {[
                  { label: 'Name', name: 'name', type: 'text', required: true, value: consultationFormData.name, onChange: (v: string) => setConsultationFormData(p => ({...p, name: v})) },
                  { label: 'Email', name: 'email', type: 'email', required: true, value: consultationFormData.email, onChange: (v: string) => setConsultationFormData(p => ({...p, email: v})) },
                  { label: 'Company', name: 'company', type: 'text', required: false, value: consultationFormData.company, onChange: (v: string) => setConsultationFormData(p => ({...p, company: v})) },
                  { label: 'Phone', name: 'phone', type: 'tel', required: false, value: consultationFormData.phone, onChange: (v: string) => setConsultationFormData(p => ({...p, phone: v})) },
                ].map(f => (
                  <div key={f.name}>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">{f.label}{f.required && ' *'}</label>
                    <input type={f.type} name={f.name} required={f.required} value={f.value} onChange={e => f.onChange(e.target.value)} className={inputCls} />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Message</label>
                  <textarea name="message" rows={3} maxLength={500} value={consultationFormData.message} onChange={e => setConsultationFormData(p => ({...p, message: e.target.value}))} className={`${inputCls} resize-none`} />
                  <p className="text-xs text-slate-400 mt-1 text-right">{consultationFormData.message.length}/500</p>
                </div>
                <button type="submit" disabled={consultationSubmitting} className="w-full bg-teal-500 text-white py-3.5 rounded-xl font-medium hover:bg-teal-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer text-sm">
                  {consultationSubmitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {showAuditForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAuditForm(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-serif text-2xl font-bold text-slate-900">Systems Audit Request</h3>
              <button onClick={() => setShowAuditForm(false)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer rounded-full hover:bg-slate-100 transition-colors">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            {auditSuccess ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-5xl text-teal-500" />
                </div>
                <p className="text-slate-700 font-medium">Thank you! We&apos;ll be in touch soon.</p>
              </div>
            ) : (
              <form id="audit-form" data-readdy-form onSubmit={handleAuditSubmit} className="space-y-4">
                {[
                  { label: 'Name', name: 'name', type: 'text', required: true, value: auditFormData.name, onChange: (v: string) => setAuditFormData(p => ({...p, name: v})) },
                  { label: 'Email', name: 'email', type: 'email', required: true, value: auditFormData.email, onChange: (v: string) => setAuditFormData(p => ({...p, email: v})) },
                  { label: 'Company', name: 'company', type: 'text', required: false, value: auditFormData.company, onChange: (v: string) => setAuditFormData(p => ({...p, company: v})) },
                  { label: 'Phone', name: 'phone', type: 'tel', required: false, value: auditFormData.phone, onChange: (v: string) => setAuditFormData(p => ({...p, phone: v})) },
                ].map(f => (
                  <div key={f.name}>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">{f.label}{f.required && ' *'}</label>
                    <input type={f.type} name={f.name} required={f.required} value={f.value} onChange={e => f.onChange(e.target.value)} className={inputCls} />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Industry</label>
                  <select name="industry" value={auditFormData.industry} onChange={e => setAuditFormData(p => ({...p, industry: e.target.value}))} className={inputCls}>
                    <option value="">Select Industry</option>
                    <option value="medical">Medical &amp; Dental</option>
                    <option value="home-services">Home Services</option>
                    <option value="legal">Legal &amp; Financial</option>
                    <option value="local-business">Small &amp; Local Business</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Message</label>
                  <textarea name="message" rows={3} maxLength={500} value={auditFormData.message} onChange={e => setAuditFormData(p => ({...p, message: e.target.value}))} className={`${inputCls} resize-none`} />
                  <p className="text-xs text-slate-400 mt-1 text-right">{auditFormData.message.length}/500</p>
                </div>
                <button type="submit" disabled={auditSubmitting} className="w-full bg-teal-500 text-white py-3.5 rounded-xl font-medium hover:bg-teal-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer text-sm">
                  {auditSubmitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
